import { getProductSamplesFromCollection } from '@lib/operations/collection'
import type { CollectionProductSample } from '@lib/types'
import type { SingleSelectionType } from '@lib/types/customization'

/**
 * Get bonus items from customization groups marked as bonus_items = true
 * Selects the first variant with isRMC/isNMC = true for each product.
 * When group.hide_from_summary is true, items are still added to the order but not shown on the confirmation summary.
 */
export async function getBonusItemSelections(
	customizationGroups: Array<{
		collection_slug: string
		bonus_items?: boolean
		max_items?: number
		hide_from_summary?: boolean
	}>,
	customizationType: 'NMC' | 'RMC'
): Promise<SingleSelectionType[]> {
	const bonusGroups = customizationGroups.filter((group) => group.bonus_items === true)

	if (bonusGroups.length === 0) {
		return []
	}

	const bonusSelections: SingleSelectionType[] = []
	const hideFromSummary = (group: (typeof bonusGroups)[0]) => group.hide_from_summary === true

	const groupPromises = bonusGroups.map(async (group) => {
		if (!group.collection_slug) {
			console.warn('Bonus group missing collection_slug:', group)
			return []
		}

		try {
			// Fetch products from the bonus item collection
			const products: CollectionProductSample[] = await getProductSamplesFromCollection(
				group.collection_slug,
				customizationType,
				0 // stockTolerance - using 0 for bonus items
			)

			// For each product, select the first variant with visible = true
			const groupSelections: SingleSelectionType[] = []
			for (const product of products) {
				const visibleVariant = product.sampleVariants.find((variant) =>
					// Check RMC or NMC visible boolean based on customization type
					customizationType === 'RMC' ? variant.isRMC === true : variant.isNMC === true
				)

				if (visibleVariant) {
					groupSelections.push({
						product,
						variant: visibleVariant,
						isBonus: true,
						hideFromSummary: hideFromSummary(group)
					})
				} else {
					// If no variant has visible = true, log a warning
					console.warn(
						`No visible variant found for bonus item product ${product.id} in collection ${group.collection_slug}`
					)
				}
			}
			return groupSelections
		} catch (error) {
			console.error(`Error fetching bonus items from collection ${group.collection_slug}:`, error)
			return []
		}
	})

	const groupResults = await Promise.all(groupPromises)
	bonusSelections.push(...groupResults.flat())

	return bonusSelections
}

/**
 * Check if a selection is a bonus item by checking if its variant ID
 * matches any bonus item variant IDs
 */
export function isBonusItem(
	variantId: string,
	bonusItemSelections: SingleSelectionType[]
): boolean {
	return bonusItemSelections.some((bonusSelection) => bonusSelection.variant.id === variantId)
}

/**
 * Get IDs of all bonus item variants for order notes
 */
export function getBonusItemIds(bonusItemSelections: SingleSelectionType[]): string[] {
	return bonusItemSelections.map((selection) => selection.variant.id)
}

/**
 * Filter out bonus item groups from customization steps
 * (they should not appear as steps in the UI)
 */
export function filterBonusGroups<T extends { bonus_items?: boolean }>(groups: T[]): T[] {
	return groups.filter((group) => !group.bonus_items)
}
